import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Filter, Users, Clock, MapPin } from 'lucide-react';
import Layout from '../Layout';
import TitleSection from '../TitleSection';
import { WorkshopManager } from '../../helpers/workshop';
import { showToast } from '../../helpers/customToast';
import { API_BASE_URL, getData } from '../../helpers/ApiSettings';
import '../../css/ateliers.css';

const FALLBACK_HERO_TITLE = 'Nos ateliers de prévention';
const FALLBACK_HERO_SUBTITLE = 'Réservez en ligne et participez à nos ateliers de prévention pour apprendre les bons réflexes et profiter pleinement de vos enfants en toute sécurité.';
const FALLBACK_INTRO_HTML = `
  <p>Nous proposons aux parents ou tout autres personnes ayant en charge un enfant des ateliers de prévention portant sur différentes thématiques. Animés par une passionnée pour des petits groupes de maximum 12 personnes, ces ateliers vous permettent de bénéficier de conseils adaptés et d'acquérir les bons réflexes pour éviter les accidents, dans un cadre convivial et dynamique.</p>
  <p>Que vous soyez parents, grands-parents, nounous, tantes, oncles… la prévention est une démarche essentielle, adoptez quelques bons réflexes bénéfiques pour la sécurité de l'enfant.</p>
`;

const WorkshopListingPage = () => {
  const [workshops, setWorkshops] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showUpcomingOnly, setShowUpcomingOnly] = useState(false);
  const [pageContent, setPageContent] = useState({
    hero_title: FALLBACK_HERO_TITLE,
    hero_subtitle: FALLBACK_HERO_SUBTITLE,
    intro_html: FALLBACK_INTRO_HTML,
  });

  useEffect(() => {
    fetchWorkshops();
    fetchCategories();
  }, [selectedCategory, showUpcomingOnly]);

  useEffect(() => {
    (async () => {
      try {
        const response = await getData('/workshop-page');
        const data = response?.data?.data;
        if (data) {
          setPageContent({
            hero_title: data.hero_title || FALLBACK_HERO_TITLE,
            hero_subtitle: data.hero_subtitle || FALLBACK_HERO_SUBTITLE,
            intro_html: data.intro_html || FALLBACK_INTRO_HTML,
          });
        }
      } catch (err) {
        // 404 = not initialized yet; keep fallbacks. Anything else also non-fatal.
      }
    })();
  }, []);

  const fetchWorkshops = async () => {
    try {
      setLoading(true);
      const filters = {
        upcoming: showUpcomingOnly
      };
      
      if (selectedCategory !== 'all') {
        filters.category = selectedCategory;
      }
      
      const workshopsData = await WorkshopManager.fetchWorkshops(filters);
      setWorkshops(workshopsData);
    } catch (error) {
      console.error('Error fetching workshops:', error);
      showToast('Erreur lors du chargement des ateliers', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const categoriesData = await WorkshopManager.fetchWorkshopCategories();
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  return (
    <Layout>
      <TitleSection title='Réservez vos Ateliers de Prévention'/>
      
      <div className="container my-5 ateliers-content">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="text-center mb-5">
              <h2 className="display-5 fw-bold mb-4">{pageContent.hero_title}</h2>
              {pageContent.hero_subtitle && (
                <p className="lead mb-4">{pageContent.hero_subtitle}</p>
              )}

              {pageContent.intro_html && (
                <div
                  className="p-4 bg-light rounded-3 text-start"
                  dangerouslySetInnerHTML={{ __html: pageContent.intro_html }}
                />
              )}
            </div>
          </div>
        </div>
        
        {/* Filters */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card shadow-sm">
              <div className="card-body">
                <div className="d-flex flex-wrap justify-content-between align-items-center">
                  <div className="mb-3 mb-md-0">
                    <h5 className="mb-2">Catégories</h5>
                    <div className="btn-group">
                      <button 
                        className={`btn ${selectedCategory === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setSelectedCategory('all')}
                      >
                        Tous
                      </button>
                      
                      {categories.map(category => (
                        <button
                          key={category.id}
                          className={`btn ${selectedCategory === category.id ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setSelectedCategory(category.id)}
                        >
                          {category.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="upcomingSwitch"
                        checked={showUpcomingOnly}
                        onChange={() => setShowUpcomingOnly(!showUpcomingOnly)}
                      />
                      <label className="form-check-label" htmlFor="upcomingSwitch">
                        Afficher uniquement les ateliers à venir
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Workshops List */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
            <p className="mt-3">Chargement des ateliers...</p>
          </div>
        ) : workshops.length === 0 ? (
          <div className="alert alert-info text-center">
            Aucun atelier disponible pour les critères sélectionnés.
          </div>
        ) : (
          <div className="row row-cols-1 row-cols-md-2 g-4">
            {workshops.map(workshop => (
              <div key={workshop.id} className="col">
                <div className="card h-100 shadow-sm">
                  {workshop.image && (
                    <div className="workshop-image-container">
                      <img 
                        src={`${API_BASE_URL}${workshop.image.url}`} 
                        className="card-img-top workshop-image" 
                        alt={workshop.title} 
                        style={{height: '250px', objectFit: 'contain'}}
                      />
                    </div>
                  )}
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h5 className="card-title">{workshop.title}</h5>
                      <span className="badge bg-primary">{workshop.reference}</span>
                    </div>
                    
                    <p className="card-text">{workshop.short_description}</p>
                    
                    <div className="mb-3">
                      <div className="d-flex align-items-center text-muted mb-2">
                        <Clock size={16} className="me-2" />
                        <span>Durée: {workshop.duration} minutes</span>
                      </div>
                      
                      {/* <div className="d-flex align-items-center text-muted mb-2">
                        <Users size={16} className="me-2" />
                        <span>Max: {workshop.max_participants || 12} participants</span>
                      </div> */}
                      
                      {workshop.location && (
                        <div className="d-flex align-items-center text-muted">
                          <MapPin size={16} className="me-2" />
                          <span>{workshop.location}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="mb-3">
                      <h6>Sessions:</h6>
                      {workshop.dates && workshop.dates.length > 0 ? (
                        <ul className="list-group list-group-flush" style={{listStyle: 'none'}}>
                          {workshop.dates.slice(0, 2).map(date => (
                            <li key={date.id} className="list-group-item px-0">
                              <div className="d-flex align-items-center">
                                <Calendar size={16} className="me-2 text-primary" />
                                <span>{formatDate(date.date)}</span>
                              </div>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-muted">Aucune session programmée</p>
                      )}
                    </div>
                    
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <span className="fs-4 fw-bold text-primary">{workshop.price ? workshop.price.toFixed(2)+'€' : workshop.price === 0 ? 'Gratuit' : 'Prix non communiqué'}</span>
                        {workshop.price > 0 && (
                          <span className="text-muted"> / personne</span>
                        )}
                      </div>
                      
                      <Link 
                        to={`/atelier/${workshop.documentId}`}
                        className="btn btn-primary"
                      >
                        En savoir plus
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* FAQ Section */}
        <div className="card mt-5">
          <div className="card-body">
            <h3 className="card-title">Questions fréquentes</h3>
            
            <div className="accordion" id="workshopFaq">
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#faqOne">
                    Comment se déroule un atelier ?
                  </button>
                </h2>
                <div id="faqOne" className="accordion-collapse collapse" data-bs-parent="#workshopFaq">
                  <div className="accordion-body">
                    Nos ateliers se déroulent en petits groupes de maximum 12 personnes. Après une présentation théorique, 
                    nous passons à la pratique avec des démonstrations concrètes. Vous pourrez poser toutes vos questions 
                    et bénéficier de conseils personnalisés.
                  </div>
                </div>
              </div>
              
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#faqTwo">
                    Comment réserver un atelier ?
                  </button>
                </h2>
                <div id="faqTwo" className="accordion-collapse collapse" data-bs-parent="#workshopFaq">
                  <div className="accordion-body">
                    Pour réserver, il vous suffit de sélectionner l'atelier qui vous intéresse, de choisir une date disponible 
                    et de procéder au paiement en ligne. Vous recevrez une confirmation par email avec tous les détails nécessaires.
                  </div>
                </div>
              </div>
              
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#faqThree">
                    Puis-je annuler ma réservation ?
                  </button>
                </h2>
                <div id="faqThree" className="accordion-collapse collapse" data-bs-parent="#workshopFaq">
                  <div className="accordion-body">
                    Vous pouvez annuler votre réservation jusqu'à 48 heures avant l'atelier et obtenir un remboursement complet. 
                    Pour toute annulation moins de 48 heures avant, des frais peuvent s'appliquer. Contactez-nous pour plus de détails.
                  </div>
                </div>
              </div>
              
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#faqFour">
                    Faut-il amener son enfant à l'atelier ?
                  </button>
                </h2>
                <div id="faqFour" className="accordion-collapse collapse" data-bs-parent="#workshopFaq">
                  <div className="accordion-body">
                    La plupart de nos ateliers sont destinés aux adultes uniquement, car ils contiennent des informations théoriques 
                    et des démonstrations sur des mannequins. Certains ateliers spécifiques peuvent accueillir les enfants - 
                    cela sera précisé dans la description de l'atelier.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* CTA Section */}
        <div className="row my-5">
          <div className="col-12">
            <div className="p-5 text-center bg-primary text-white rounded-3">
              <h2 className="mb-4">Prêt à participer à nos ateliers ?</h2>
              <p className="lead mb-4">Rejoignez nos ateliers de prévention en petits groupes et bénéficiez d'un suivi personnalisé</p>
              <a href="/contact" className="btn btn-light btn-lg px-4">Des questions ? Contactez-nous</a>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default WorkshopListingPage;